/**
 * Database Schema Model for FreshCards
 * Database structure with schema, views, and items
 *
 * NOTE: ViewType and ViewConfig are re-exported (aliased to DatabaseViewConfig) from
 * src/ui/components/database/viewConfig.ts, which is the runtime champion with all
 * 6 view types (gallery, kanban, list, calendar, timeline, recent).
 * This eliminates the prior 3-source drift: Database.ts had 4 types,
 * src/types/database/item.ts had 5 types (gallery, board, list, calendar, table
 * — using 'board'/'table' instead of 'kanban'/'timeline'/'recent'), and the
 * runtime champion had 6. Standardized on the runtime champion.
 */

import type { DatabaseViewConfig } from '../../ui/components/database/viewConfig';
import type { PropertyDefinition, PropertyValue, SelectOption, StatusOption, NumberFormat } from './Property';

export interface Database {
  id: string;
  name: string;
  icon?: string;
  cover?: CardCover;
  schema: DatabaseSchema;
  views: DatabaseViewConfig[];
  items: DatabaseItem[];
  /**
   * F 470 — THE ITEMS ARE PAGED, AND THE RECORD SAYS SO.
   *
   * Present when the database owns more items than one value can carry.
   * `items` is then empty and the pages ARE the items: the record holds the
   * paging descriptor, and the pages live under `prefix`.
   */
  itemPages?: DatabaseItemPages;
  createdAt: Date;
  updatedAt: Date;
  /**
   * F 499 — THE RECORD'S VERSION.
   *
   * An integer, starting at 1. It is the record's own revision counter, and
   * it is what the stale-write guard compares against: a client that sends
   * a version OLDER than the server's is refused with 409 rather than
   * overwriting a newer record.
   *
   * ⇒⇒⇒ OPTIONAL on the wire and absent on every pre-F-499 record, so an
   * old client and an old record keep working unchanged.
   */
  version?: number;
}

/**
 * F 470 — a database whose items are too many for one value.
 *
 * MEASURED 2026-10-23: HQ Backup's 16,783 items are 18,129,454 characters
 * together, and the FC adapter caps a request body at 8 MB
 * (`MAX_BODY` in the FCC API). One value cannot hold them, so the record
 * carries the PAGING DESCRIPTOR and the pages live in keys the database
 * owns — the same shape as an inline database, with a manifest instead of
 * an array.
 */
export interface DatabaseItemPages {
  /** The key prefix the pages live under. The database OWNS them. */
  prefix: string;
  /** How many pages. */
  pageCount: number;
  /** Items per page. The last page may be short. */
  pageSize: number;
  /** Total items across every page. */
  total: number;
  /** Per-kind counts, carried over from the pre-migration manifest. */
  counts?: Record<string, number>;
  /** The rollup digest of the items, so a move can be verified. */
  rollupSha256?: string;
  /** Who moved them, and when. */
  migratedBy?: string;
}

export interface DatabaseSchema {
  id: string;
  properties: PropertyDefinition[];
}

export interface DatabaseItem {
  id: string;
  databaseId: string;
  title: string;
  icon?: string;
  cover?: CardCover;
  properties: PropertyValue[];
  tags: string[];   // flat tag list for display in peek Details tab
  createdAt: Date;
  updatedAt: Date;
}

// Canonical ViewType + DatabaseViewConfig (re-exported and aliased as ViewConfig
// for back-compat at legacy import sites). Single source of truth is the runtime
// champion in src/ui/components/database/viewConfig.ts.
export type {
  ViewType,
  DatabaseViewConfig as ViewConfig,
} from '../../ui/components/database/viewConfig';

export interface CardCover {
  type: 'external' | 'file';
  url: string;
}

export interface Filter {
  propertyId: string;
  operator: FilterOperator;
  value: unknown;
}

export type FilterOperator = 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'greater_than' | 'less_than';

export interface Sort {
  propertyId: string;
  direction: 'asc' | 'desc';
}

/**
 * Creates a new database with default schema and view
 */
export function createDatabase(id: string, name: string = 'Untitled Database'): Database {
  const now = new Date();
  return {
    id,
    name,
    schema: {
      id: `${id}_schema`,
      properties: [],
    },
    views: [
      createDefaultView(id, 'list'),
    ],
    items: [],
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Creates a default view configuration (canonical DatabaseViewConfig shape).
 * @param databaseId — required for the canonical DatabaseViewConfig shape (parent database).
 * @param type — one of the 6 canonical view types.
 */
export function createDefaultView(databaseId: string, type: ViewType = 'list'): DatabaseViewConfig {
  const now = Date.now();
  return {
    id: `${databaseId}_default_view`,
    databaseId,
    type,
    name: type.charAt(0).toUpperCase() + type.slice(1),
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Creates a gallery view (canonical DatabaseViewConfig shape).
 */
export function createGalleryView(databaseId: string, name: string = 'Gallery', id?: string): DatabaseViewConfig {
  const now = Date.now();
  return {
    id: id || `${databaseId}_gallery_view`,
    databaseId,
    type: 'gallery',
    name,
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Creates a kanban view (canonical DatabaseViewConfig shape).
 * groupByPropertyId maps to the canonical `groupBy: GroupByMode` field.
 */
export function createKanbanView(databaseId: string, name: string = 'Board', groupByPropertyId?: string, id?: string): DatabaseViewConfig {
  const view = createDefaultView(databaseId, 'kanban');
  view.id = id || `${databaseId}_kanban_view`;
  view.name = name;
  if (groupByPropertyId) {
    view.groupBy = 'status'; // canonical GroupByMode
  }
  return view;
}

/**
 * Creates a calendar view (canonical DatabaseViewConfig shape).
 * datePropertyId maps to the canonical `datePropId: string` field.
 */
export function createCalendarView(databaseId: string, name: string = 'Calendar', datePropertyId?: string, id?: string): DatabaseViewConfig {
  const view = createDefaultView(databaseId, 'calendar');
  view.id = id || `${databaseId}_calendar_view`;
  view.name = name;
  if (datePropertyId) {
    view.datePropId = datePropertyId;
  }
  return view;
}

/**
 * Creates a database item
 */
export function createDatabaseItem(id: string, databaseId: string, title: string = 'Untitled'): DatabaseItem {
  const now = new Date();
  return {
    id,
    databaseId,
    title,
    properties: [],
    tags: [],
    createdAt: now,
    updatedAt: now,
  };
}